import React, { Component } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.min.css";

import { getCookieData } from "../../../utils/CommonFunction";
import { getHeaders } from "../../../utils/apiHelper";
import {
  leaveCarryModuleData,
  calculatedLeaveBalance,
  startLeaveCarry,
  outstandingLeaves,
  approveOutstandingLeaves,
  rejectOutstandingLeaves,
  calculateEarnedLeaveCarry,
  confirmAndSaveEarnedLeaveCarryBalance,
} from "../../../utils/apiRoutes";
import HeadingText from "./components/HeadingText";
import LeaveCarryTargetYear from "./components/LeaveCarryTargetYear";
import LeaveCategoryTable from "./leave_categories/LeaveCategoryTable";
import CalculateEarnedLeavesButton from "./earned_leaves_calculations/CalculateEarnedLeavesButton";
import LeaveCarryProcessStartButton from "./components/LeaveCarryProcessStartButton";
import ViewPreviousLeaveBalanceButton from "./previous_leave_balance/ViewPreviousLeaveBalanceButton";
import LeaveBalanceCalculationStatistics from "./components/LeaveBalanceCalculationStatistics";
import PreviousLeaveBalanceTable from "./previous_leave_balance/PreviousLeaveBalanceTable";
import OutstandingLeaves from "./outstanding_leaves/OutstandingLeaves";
import OutstandingLeavesStatistics from "./outstanding_leaves/OutstandingLeavesStatistics";
import OutstandingLeavesTable from "./outstanding_leaves/OutstandingLeavesTable";
import CalculatedEarnedLeaveBalanceTable from "./earned_leaves_calculations/CalculatedEarnedLeaveBalanceTable";
import LoadingIndicator from "../../Common/components/LoadingIndicator";

export default class EarnedLeaveCarry extends Component {
  constructor(props) {
    super(props);
    this.state = {
      user_info: getCookieData("user_info"),
      leaveCarryProcessStatus: [],
      leaveCategories: [],
      leaveCarryCalculatedYears: [],
      previousCalculations: [],
      designations: [],
      departments: [],
      branches: [],
      regions: [],
      employees: [],
      leaveApplicationStatusList: [],
      isLeaveCarryStart: false,
      outstandingLeaves: [],
      hasFetchedOutstandingLeaves: false,
      calculatedResult: [],
      hasClickedCalculate: false,
      hasClickedConfirmAndSave: false,
      isLoading: true,
    };
  }

  get currentLeaveCarryYears() {
    const latestLeaveCarryYears = this.state.leaveCarryCalculatedYears.reduce(
      (prev, curr, _, __) => {
        return curr.yearStart > prev.yearStart ? curr : prev;
      }
    );

    const monthStart =
      latestLeaveCarryYears.monthEnd === 12
        ? 1
        : latestLeaveCarryYears.monthEnd + 1;

    return {
      yearStart: latestLeaveCarryYears.yearStart + 1,
      monthStart: monthStart,
      yearEnd: latestLeaveCarryYears.yearStart + 2,
      monthEnd: 3,
    };
  }

  // This controls when the leave carry process start button will be disabled or enabled.
  // Turn on the commented line below for testing.
  get isAlreadyCalculatedForThisFiscalYear() {
    if (this.state.leaveCarryCalculatedYears.length === 0) return false;
    const { yearEnd, monthEnd } = this.currentLeaveCarryYears;
    const today = new Date();
    // const today = new Date(2025, 3, 1); // ***** FISCAL LEAVE DATE INJECTION *****

    return (
      today.getFullYear() < yearEnd ||
      (today.getFullYear() === yearEnd && today.getMonth() + 1 < monthEnd)
    );
  }

  get outstandingEarnedLeaves() {
    return this.state.outstandingLeaves.filter((leave) => {
      return leave.leave_category_id === 5;
    });
  }

  async componentDidMount() {
    const { data: moduleData } = await this.getModuleData();
    if (!!moduleData) this.initModuleData(moduleData);
  }

  getModuleData = async () => {
    const res = await fetch(leaveCarryModuleData, {
      headers: getHeaders(),
    });
    if (res.ok) {
      return res.json();
    } else {
      this.setState({
        isLoading: false,
      });
      return null;
    }
  };

  initModuleData = async (moduleData) => {
    const isEarnedLeaveCarryInProcess = moduleData.leaveCarryProcessStatus.find(
      (status) => status.leave_category_id === 5
    ).in_carry_process;
    if (isEarnedLeaveCarryInProcess) {
      const res = await fetch(outstandingLeaves, {
        headers: getHeaders(false),
      });
      if (!res.ok) {
        this.setState({
          isLoading: false,
        });
        return toast.error("Error: Could not fetch outstanding leaves.");
      }
      const { data } = await res.json();
      this.setState({
        isLeaveCarryStart: true,
        outstandingLeaves: data,
        hasFetchedOutstandingLeaves: true,
      });
    }

    moduleData.leaveCarryCalculatedYears.sort(function (a, b) {
      return b.yearStart - a.yearStart;
    });

    moduleData.designations.unshift({ label: "ALL", value: 0 });
    moduleData.departments.unshift({ label: "ALL", value: 0 });
    moduleData.branches.unshift({ label: "ALL", value: 0 });
    moduleData.regions.unshift({ label: "ALL", value: 0 });
    moduleData.leaveApplicationStatusList.unshift({ label: "ALL", value: 0 });

    const employees = moduleData.employees.map((employee) => {
      return {
        label: `(${employee.employment_id}) ${employee.label}`,
        value: employee.value,
      };
    });
    employees.unshift({ label: "ALL", value: 0 });

    this.setState({
      leaveCarryProcessStatus: moduleData.leaveCarryProcessStatus,
      leaveCategories: moduleData.leaveCategories,
      leaveCarryCalculatedYears: moduleData.leaveCarryCalculatedYears,
      designations: moduleData.designations,
      departments: moduleData.departments,
      branches: moduleData.branches,
      regions: moduleData.regions,
      employees: employees,
      leaveApplicationStatusList: moduleData.leaveApplicationStatusList,
      isLoading: false,
    });
  };

  viewLeaveBalance = async (year) => {
    // 1) Check if year is selected
    if (!year) {
      return toast.error("Select a year to view the calculated leave balance.");
    }

    // 2) Get data
    const res = await fetch(calculatedLeaveBalance(year), {
      headers: getHeaders(),
    });
    if (!res.ok) {
      return toast.error("Something went wrong. Unable to get data.");
    }
    const { data } = await res.json();

    this.setState({
      previousCalculations: data,
    });
  };

  startLeaveCarryProcess = async () => {
    this.setState({
      isLeaveCarryStart: true,
    });

    const res = await fetch(startLeaveCarry, {
      method: "post",
      headers: getHeaders(false),
    });
    if (!res.ok) {
      const { message } = await res.json();
      this.setState({
        isLeaveCarryStart: false,
      });

      return toast.error(
        message ??
          "Error: Could not start the leave carry process or fetch outstanding leaves."
      );
    }
    const { data } = await res.json();
    this.setState({
      outstandingLeaves: data,
      hasFetchedOutstandingLeaves: true,
    });
  };

  updateLeave = async (leaveApplicationIds, url, action) => {
    const res = await fetch(url, {
      method: "post",
      headers: getHeaders(false),
      body: JSON.stringify({
        leaveApplicationIds,
        updateUserId: this.state.user_info.user_id,
      }),
    });

    const { data: remainingOutstandingLeavesIds } = await res.json();
    toast.success(
      leaveApplicationIds.length === 1
        ? `1 leave application has been ${action}.`
        : `${leaveApplicationIds.length} leave applications have been ${action}.`
    );
    const remainingOutstandingLeaves = this.state.outstandingLeaves.filter(
      (leave) => {
        return remainingOutstandingLeavesIds.includes(
          leave.leave_application_id
        );
      }
    );
    this.setState({
      outstandingLeaves: remainingOutstandingLeaves,
    });
  };

  approveLeaves = async (leaves) => {
    if (leaves.length === 0) return;

    let invalidLeaveUser = undefined;
    for (let i = 0; i < leaves.length; i++) {
      const leave = leaves[i];

      if (leave.leave_days + leave.leaves_taken > leave.leaves_available) {
        invalidLeaveUser = leave.fullname;
        break;
      }
    }
    if (invalidLeaveUser)
      return toast.error(
        `One or more leaves by ${invalidLeaveUser} exceed the allowed days!`
      );

    await this.updateLeave(
      leaves.map((leave) => leave.leave_application_id),
      approveOutstandingLeaves,
      "approved"
    );
  };

  rejectLeaves = async (leaves) => {
    if (leaves.length === 0) return;

    await this.updateLeave(
      leaves.map((leave) => leave.leave_application_id),
      rejectOutstandingLeaves,
      "rejected"
    );
  };

  calculate = async (leaveCategory) => {
    // Accept leaveCategory so that this method is also flexible to other leave categories in future.

    this.setState({
      hasClickedCalculate: true,
    });

    const res = await fetch(calculateEarnedLeaveCarry, {
      headers: getHeaders(),
    });
    if (!res.ok) {
      return toast.error(
        "Something went wrong. Unable to calculate earned leave to carry forward."
      );
    }
    toast.success(
      "Calculation successful! Please review the results and click the 'Confirm and Save' button to complete the process.",
      {
        autoClose: 7000,
      }
    );

    const { data } = await res.json();
    this.setState({
      calculatedResult: data,
    });
  };

  confirmAndSave = async () => {
    this.setState({
      hasClickedConfirmAndSave: true,
    });

    const res = await fetch(confirmAndSaveEarnedLeaveCarryBalance, {
      method: "post",
      headers: getHeaders(false),
    });
    if (!res.ok) {
      const { message } = await res.json();
      this.setState({
        hasClickedConfirmAndSave: false,
      });

      return toast.error(
        message ?? "Something went wrong. Unable to save data."
      );
    }
    const { data } = await res.json();
    const { data: moduleData } = await this.getModuleData();
    this.setState({
      calculatedResult: [],
      previousCalculations: [],
      isLeaveCarryStart: false,
      outstandingLeaves: [],
      hasFetchedOutstandingLeaves: false,
      hasClickedCalculate: false,
      hasClickedConfirmAndSave: false,
    });

    if (!!moduleData) this.initModuleData(moduleData);

    toast.success(data, {
      autoClose: 7000,
    });
  };

  render() {
    if (this.state.isLoading) return <LoadingIndicator />;

    return (
      <div>
        <ToastContainer position={toast.POSITION.TOP_RIGHT} />
        <div className="row wrapper white-bg border-bottom">
          <HeadingText>
            {this.state.leaveCarryCalculatedYears.length !== 0 && (
              <LeaveCarryProcessStartButton
                startLeaveCarryProcess={this.startLeaveCarryProcess}
                disabled={
                  this.isAlreadyCalculatedForThisFiscalYear ||
                  this.state.isLeaveCarryStart ||
                  this.state.leaveCarryCalculatedYears.length === 0
                }
                hasFetchedData={
                  this.state.leaveCarryCalculatedYears.length !== 0
                }
                alreadyCalculated={this.isAlreadyCalculatedForThisFiscalYear}
                isLeaveCarryStart={this.state.isLeaveCarryStart}
                currentLeaveCarryYears={this.currentLeaveCarryYears}
              />
            )}
          </HeadingText>
          {this.state.leaveCarryCalculatedYears.length !== 0 && (
            <LeaveCarryTargetYear
              currentLeaveCarryYears={this.currentLeaveCarryYears}
            />
          )}

          {this.outstandingEarnedLeaves.length === 0 &&
            this.state.leaveCategories &&
            this.state.leaveCategories.length !== 0 && (
              <LeaveCategoryTable
                leaveCategories={this.state.leaveCategories}
              />
            )}

          {this.state.isLeaveCarryStart &&
            this.state.hasFetchedOutstandingLeaves &&
            this.state.leaveCarryCalculatedYears.length !== 0 && (
              <CalculateEarnedLeavesButton
                leaveCategories={this.state.leaveCategories}
                calculate={this.calculate}
                disableCalculate={
                  this.state.hasClickedCalculate ||
                  this.state.calculatedResult.length !== 0
                }
                confirmAndSave={
                  this.state.calculatedResult.length === 0
                    ? null
                    : this.confirmAndSave
                }
                disableSave={this.state.hasClickedConfirmAndSave}
                currentLeaveCarryYears={this.currentLeaveCarryYears}
                outstandingEarnedLeavesCount={
                  this.outstandingEarnedLeaves.length
                }
              />
            )}

          {!this.state.isLeaveCarryStart && (
            <ViewPreviousLeaveBalanceButton
              leaveCarryCalculatedYears={this.state.leaveCarryCalculatedYears}
              viewLeaveBalance={this.viewLeaveBalance}
            />
          )}

          {!this.state.isLeaveCarryStart && (
            <LeaveBalanceCalculationStatistics
              data={this.state.previousCalculations}
            />
          )}
          {!this.state.isLeaveCarryStart && (
            <PreviousLeaveBalanceTable
              data={this.state.previousCalculations}
              designations={this.state.designations}
              departments={this.state.departments}
              branches={this.state.branches}
              regions={this.state.regions}
            />
          )}

          {this.state.isLeaveCarryStart &&
            this.state.outstandingLeaves.length !== 0 &&
            this.state.calculatedResult.length === 0 && (
              <OutstandingLeaves>
                <OutstandingLeavesStatistics
                  outstandingLeaves={this.state.outstandingLeaves}
                />
                <OutstandingLeavesTable
                  outstandingLeaves={this.state.outstandingLeaves}
                  leaveCategories={this.state.leaveCategories}
                  designations={this.state.designations}
                  departments={this.state.departments}
                  branches={this.state.branches}
                  regions={this.state.regions}
                  employees={this.state.employees}
                  leaveApplicationStatusList={
                    this.state.leaveApplicationStatusList
                  }
                  approveLeaves={this.approveLeaves}
                  rejectLeaves={this.rejectLeaves}
                />
              </OutstandingLeaves>
            )}

          {this.state.isLeaveCarryStart &&
            this.state.calculatedResult.length !== 0 && (
              <LeaveBalanceCalculationStatistics
                data={this.state.calculatedResult}
              />
            )}

          {this.state.isLeaveCarryStart &&
            this.state.calculatedResult.length !== 0 && (
              <CalculatedEarnedLeaveBalanceTable
                data={this.state.calculatedResult}
                designations={this.state.designations}
                departments={this.state.departments}
                branches={this.state.branches}
                regions={this.state.regions}
              />
            )}
        </div>
      </div>
    );
  }
}
